// useDashboardSession — returns the current dashboard user's role + scopes.
//
// Source order (first available wins):
//   1. URL query ?role=officer                       — dev/QA cross-role test
//   2. localStorage["location.dashboard.session"]  — last seen session
//   3. fetch /v1/auth/me with nexus_session cookie      — self-built auth
//                                                       (Google/Apple OAuth
//                                                       or email + password)
//
// /v1/auth/me is the single source of truth for "who am I". The legacy
// Cloudflare Access PIN gate that used to wrap the dashboard at the
// Cloudflare edge has been removed (the `location-admin` Access app
// was deleted from the Zero Trust dashboard); native auth-api owns the
// session lifecycle end-to-end.
//
// Until SSO lands (post-MVP) we accept (1) so QA can verify nav filtering
// and route guards across all 5 roles via deep links like:
//   https://nexus.sentry.red/dashboard?role=officer
//
// The full session shape mirrors the LIFF JWT claims so server-side scope
// enforcement is identical.

import React, { createContext, useContext, useEffect, useState } from "react";
import { fetchMe, hasSessionHint, type MeResponse } from "../../lib/auth.js";
import { mapMeToDashboardSession } from "./mapMeToDashboardSession.js";

// localStorage shape version. Bump when DashboardSession adds / removes
// fields that gate behaviour (e.g. PR #565 introduced `permissions` —
// stale v1 entries left super_admins with `permissions: []` which
// tripped the new permission-based RequireRole into a /forbidden flash
// on first paint before /v1/auth/me could upgrade). Old keys are
// effectively invisible so the hydrate path repopulates fresh.
const STORAGE_KEY = "location.dashboard.session.v2";
const LEGACY_STORAGE_KEYS = ["location.dashboard.session"] as const;

export interface DashboardSession {
  tenant_id: string;
  user_id: string | null;
  role: string;       // "tenant_admin" | "manager" | "officer" | "incident_reporter" | "auditor" | "super_admin"
  scopes: string[];   // legacy Scope[] — kept for backwards-compat readers
  /** Permission ids (Custom RBAC §4). Computed server-side from
   *  user_role_assignments union or legacy role fallback. Future
   *  permission-based RequireRole / sidebar guards read from here. */
  permissions: string[];
  email?: string;
  display_name?: string;
  /** True when role=super_admin (cross-tenant SENTRY staff). */
  super_admin?: boolean;
  /** When a super_admin chose to act as a tenant, the chosen tenant id. */
  acting_tenant_id?: string;
  /** Human-readable tenant name to render in the impersonation chip. */
  acting_tenant_name?: string;
}

const DEFAULT_GUEST: DashboardSession = {
  tenant_id: "",
  user_id: null,
  role: "guest",
  scopes: [],
  permissions: [],
};

interface DashboardSessionContextValue {
  session: DashboardSession;
  /**
   * True while the /v1/auth/me bootstrap is in flight AND we do not
   * yet have a definitive session — i.e. the user might be logged in,
   * the SPA just hasn't heard back from auth-api yet.
   *
   * RequireRole gates on this so the immediate post-OAuth-redirect
   * doesn't bounce the user to /dashboard/forbidden because the
   * synchronous initial state is still the guest sentinel.
   *
   * Hydration only fires when:
   *   (a) we don't have a stored localStorage session, AND
   *   (b) the non-httpOnly hint cookie nexus_session_present=1 IS set
   *       (so we believe a login just happened or persists), AND
   *   (c) we're not in the QA ?role= override mode.
   * Otherwise we go straight to the resolved value (no flash of loader).
   */
  isHydrating: boolean;
}

const DashboardSessionContext = createContext<DashboardSessionContextValue>({
  session: DEFAULT_GUEST,
  isHydrating: false,
});

/** Provider — hydrates session from localStorage / URL on mount, then
 *  upgrades via /v1/auth/me using the nexus_session httpOnly cookie. */
export function DashboardSessionProvider({ children }: { children: React.ReactNode }): React.ReactElement {
  const [session, setSession] = useState<DashboardSession>(() => loadInitialSession());

  // Hydrating sentinel — see context-value JSDoc above for the rules.
  // Computed synchronously on initial render so RequireRole's first
  // pass has the right answer (no spurious /forbidden bounce).
  const [isHydrating, setIsHydrating] = useState<boolean>(() => {
    if (typeof window === "undefined") return false;
    // QA ?role= override — never hydrate, mock is authoritative.
    if (new URL(window.location.href).searchParams.get("role")) return false;
    // Already have a real localStorage session — first render is correct.
    if (loadInitialSession().role !== "guest") return false;
    // Hint cookie present → we expect /me to upgrade us shortly. Mark
    // hydrating so RequireRole holds back until we hear from auth-api.
    return hasSessionHint();
  });

  // Listen for cross-tab session updates
  useEffect(() => {
    const onStorage = (e: StorageEvent) => {
      if (e.key === STORAGE_KEY) setSession(loadInitialSession());
    };
    window.addEventListener("storage", onStorage);
    return () => window.removeEventListener("storage", onStorage);
  }, []);

  // /v1/auth/me bootstrap. Runs on every mount that didn't already have
  // a real (non-guest, non-?role= override) session in localStorage.
  // We always re-validate because a stored session could be stale.
  useEffect(() => {
    // Skip when the QA ?role= override was used — operator wants the mock.
    if (typeof window !== "undefined" && new URL(window.location.href).searchParams.get("role")) {
      setIsHydrating(false);
      return;
    }
    let cancelled = false;
    void fetchMe()
      .then((me) => {
        if (cancelled) return;
        if (!me) {
          if (session.role !== "guest") {
            clearDashboardSession();
            setSession(DEFAULT_GUEST);
          }
          return;
        }
        const next = mapMeToDashboardSession(me);
        persistDashboardSession(next);
        setSession(next);
      })
      .catch((e) => {
        // eslint-disable-next-line no-console
        console.warn("[dashboard] /v1/auth/me bootstrap skipped:", (e as Error).message ?? e);
      })
      .finally(() => {
        if (!cancelled) setIsHydrating(false);
      });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <DashboardSessionContext.Provider value={{ session, isHydrating }}>
      {children}
    </DashboardSessionContext.Provider>
  );
}

export function useDashboardSession(): DashboardSession {
  return useContext(DashboardSessionContext).session;
}

/**
 * Read the hydrating sentinel — true while /v1/auth/me is in flight on
 * a fresh load + we don't yet have a definitive session in localStorage.
 * RequireRole uses this to avoid a /forbidden flash during the post-
 * OAuth-redirect bootstrap window (~100-300 ms on a warm cache).
 */
export function useDashboardSessionHydrating(): boolean {
  return useContext(DashboardSessionContext).isHydrating;
}

export function useDashboardRole(): string {
  return useDashboardSession().role;
}

export function useDashboardScope(...required: string[]): boolean {
  const { scopes } = useDashboardSession();
  const have = new Set(scopes);
  if (have.has("admin:platform")) return true;
  if (have.has("admin:tenant")) return true;
  for (const r of required) if (!have.has(r)) return false;
  return true;
}

// ─── Helpers ─────────────────────────────────────────────────────────────

function loadInitialSession(): DashboardSession {
  // 1. ?role= query (dev/QA cross-role test) — takes precedence so a QA
  //    URL always renders deterministically even if a real session is
  //    in localStorage.
  if (typeof window !== "undefined") {
    const url = new URL(window.location.href);
    const qsRole = url.searchParams.get("role");
    if (qsRole) {
      return mockSessionForRole(qsRole, url.searchParams.get("tenant") ?? "01HZTEST00000000000000DEMO0");
    }
  }
  // 2. localStorage. Belt-and-suspenders: even if a v1-shape blob
  //    sneaks in under the v2 key (developer reused the key, etc.),
  //    reject sessions missing the `permissions` array — they trigger
  //    a /forbidden flash on first render against the permission-
  //    based RequireRole. Fall through to guest → hydrate.
  if (typeof localStorage !== "undefined") {
    try {
      const raw = localStorage.getItem(STORAGE_KEY);
      if (raw) {
        const parsed = JSON.parse(raw) as DashboardSession;
        if (parsed && typeof parsed.role === "string" && Array.isArray(parsed.permissions)) {
          return parsed;
        }
      }
    } catch { /* fall through */ }
  }
  return DEFAULT_GUEST;
}

/** For QA / dev only — synthesises a session given a role string. */
export function mockSessionForRole(role: string, tenantId: string): DashboardSession {
  const SCOPES_BY_ROLE: Record<string, string[]> = {
    tenant_admin: ["admin:tenant"],          // wildcard
    manager: [
      "read:events", "write:events",
      "read:incident", "write:incident", "ack:incident",
      "read:patrol", "write:patrol",
      "read:schedule", "write:schedule",
      "read:report:officer", "read:report:site", "read:report:tenant",
      "read:site", "write:site",
      "read:user",
      "read:notification", "write:notification",
      "read:tenant", "bind:line",
    ],
    officer: [
      "read:events", "read:incident", "write:incident",
      "read:patrol:self", "write:patrol", "read:schedule:self",
      "read:report:officer", "read:site", "bind:line",
    ],
    incident_reporter: ["write:incident", "read:incident:self", "bind:line"],
    auditor: [
      "read:events", "read:incident", "read:patrol", "read:schedule",
      "read:report:officer", "read:report:site", "read:report:tenant",
      "read:site", "read:user", "read:billing", "read:notification",
      "read:tenant", "read:audit",
    ],
    super_admin: [
      "admin:platform",          // platform wildcard
      "admin:tenant",
      "read:tenants", "write:tenants", "switch:tenant",
    ],
  };
  return {
    tenant_id: tenantId,
    user_id: `qa_${role}`,
    role,
    scopes: SCOPES_BY_ROLE[role] ?? [],
    // QA override session: skip permission population — these sessions are
    // for layout / role-gate QA only, not permission-sensitive flows.
    permissions: [],
    email: role === "super_admin" ? "ksbcboy@gmail.com" : `qa-${role}@location.local`,
    display_name: role === "super_admin" ? "Dalton (super_admin)" : `QA ${role}`,
    super_admin: role === "super_admin" ? true : undefined,
  };
}

/** Persist a session (used after real SSO returns). Also evicts legacy
 *  storage keys so a stale v1 entry can't reappear in a future load
 *  (browsers sometimes restore values from sync). */
export function persistDashboardSession(session: DashboardSession): void {
  if (typeof localStorage !== "undefined") {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(session));
    for (const k of LEGACY_STORAGE_KEYS) localStorage.removeItem(k);
  }
}

export function clearDashboardSession(): void {
  if (typeof localStorage !== "undefined") {
    localStorage.removeItem(STORAGE_KEY);
    for (const k of LEGACY_STORAGE_KEYS) localStorage.removeItem(k);
  }
}
